import SubmitContext from 'context/submit/submitContext'
import PageContext from 'context/page/pageContext'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useState, useRef } from 'react'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import { useFormContext } from 'react-hook-form'
import { oatVarietyOptions as brownRiceOatVarietyOptions } from 'views/WholeGrains/BrownRice/constants'
import {
  oatVarietyOptions as oatsOatmealOatVarietyOptions,
  oatTypeOptions as oatsOatmealOatTypeOptions,
} from 'views/WholeGrains/OatsOatmeal/constants'
import {
  fruitTypeOptions as frozenFruitTypeOptions,
  vegTypeOptions as frozenVegTypeOptions,
  packageTypeOptions as frozenPackageTypeOptions,
} from 'views/FruitsAndVegs/FrozenVegsFruits/constants'
import {
  fruitTypeOptions as cannedFruitTypeOptions,
  vegTypeOptions as cannedVegTypeOptions,
  fruitContainerTypeOptions as cannedFruitContainerTypeOptions,
  vegContainerTypeOptions as cannedVegContainerTypeOptions,
} from 'views/FruitsAndVegs/CannedVegsFruits/constants'
import { textureOptions as peanutButterTextureOptions } from 'views/Legumes/PeanutButter/constants'
import {
  driedFruitsTypeOptions,
  packageTypeOptions as driedFruitsPackageTypeOptions,
} from 'views/FruitsAndVegs/DriedFruits/constants'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Table,
  UncontrolledTooltip,
} from 'reactstrap'
import NotificationAlert from 'react-notification-alert'
import { SUBMITTED_STATUS } from 'variables/categories'
import AuthContext from 'context/auth/authContext'

const SubmissionList = ({
  productTitle,
  category,
  subCategory,
  model,
  validCategories,
  validSubCategories,
}) => {
  const methods = useFormContext()

  const submitContext = useContext(SubmitContext)
  const {
    editCurrent,
    deleteProduct,
    filtered,
    updateSubmitterProduct,
    getContactProducts,
    products,
    filterProducts,
    setCurrent,
    setUpdate,
    updating,
  } = submitContext

  const pageContext = useContext(PageContext)
  const { setScrollToTop } = pageContext

  const authContext = useContext(AuthContext)
  const { user } = authContext

  const [alert, setAlert] = useState(null)

  const editProduct = UPC => {
    editCurrent(UPC)
  }

  const hideAlert = () => {
    setAlert(null)
  }

  const deleteConfirmed = UPC => {
    hideAlert()
    deleteProduct(
      filtered.find(Target => Target.UPC === UPC),
      UPC
    )
  }

  const deleteCurrent = UPC => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: 'block', marginTop: '+200px' }}
        title={`Deleting product ${UPC}. Are you sure?`}
        onConfirm={() => {
          deleteConfirmed(UPC)
          setCurrent(model)
          setUpdate(false)
        }}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="info"
        cancelBtnBsStyle="danger"
        confirmBtnText="Yes, delete it!"
        cancelBtnText="Cancel"
        showCancel
      >
        You will not be able to recover this product!
      </ReactBSAlert>
    )
  }

  // Case: Initial load
  useEffect(() => {
    if (products.length === 0 && user) {
      console.log('user is: ' + JSON.stringify(user))
      getContactProducts(user?.pkContact)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Case: After editing
  useEffect(() => {
    if (user) {
      getContactProducts(user?.pkContact).then(
        filterProducts(validCategories, validSubCategories)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updating])

  // Case: After submission or deletion
  useEffect(() => {
    /* Caution: If getVendorProducts/getContactProducts is called with products as a dependency, will cause an infinite loop.
    If product.length > 0 for filterProducts to be called, then it will not be called when we delete a product resulting in no products. */

    filterProducts(validCategories, validSubCategories)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products])

  const notificationAlert = useRef()
  const notify = () => {
    let options = {
      place: 'bc',
      message: (
        <div>
          <div>
            You have submitted {filtered.length} product
            {filtered.length > 1 ? 's' : ''}.
          </div>
        </div>
      ),
      type: 'info',
      icon: 'nc-icon nc-bell-55',
      autoDismiss: 7,
    }
    notificationAlert.current.notificationAlert(options)
  }

  const submitProductList = e => {
    e.preventDefault()
    filtered.forEach(product => {
      product.fkApprovalStatus = SUBMITTED_STATUS
      updateSubmitterProduct(product)
    })
    notify()
    setCurrent(model)
  }

  return (
    <>
      <NotificationAlert ref={notificationAlert} />
      <Card>
        <CardHeader>
          <CardTitle className="float-left" tag="h5">
            {productTitle} Products to Submit
          </CardTitle>
        </CardHeader>
        <CardBody>
          {alert}
          <Table size="sm">
            <thead className="text-primary">
              <tr>
                <th>UPC</th>
                <th>Brand</th>
                <th>Product</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered?.map(prod => {
                let {
                  UPC,
                  brandName,
                  BrandName,
                  Manufacturer,
                  brandNameSelect,
                  productName,
                  ProductName,
                  distbInCA,
                  DistbInCA,
                  RetailPrice,
                  FederalNutrition,
                  fkCategory,
                  fkSubCategory,
                  FatLevel,
                  Type,
                  MadeInUSA,
                  Kosher,
                  FishType,
                  PackedIn,
                  Grade,
                  Size,
                  ContainerSize,
                  PBContainerSize,
                  PBTexture,
                  PBType,
                  BeanPeaLentilType,
                  BeanPeaLentilSize,
                  SaltContent,
                  BreadType,
                  BreadSize,
                  GrainSize,
                  OatVariety,
                  CookingType,
                  OatType,
                  BarleyVerification,
                  WholeMilkType,
                  WholeMilkSize,
                  ReducedFatMilkType,
                  ReducedFatMilkSize,
                  ContainerType,
                  VitaminCCertification,
                  OneHundredPercentJuiceCertification,
                  FrontLabelCertification,
                  FrozenPackageCertification,
                  PackageSize,
                  JuiceFlavor,
                  PackageType,
                  Organic,
                  GrainLength,
                  OrganicVerification,
                  ItemType,
                  Packaged,
                  FruitPackageCertification,
                  VegetablePackageCertification,
                  DFType,
                  DFPackageType,
                  DFSize,
                  NoAdditionalIngredients,
                  MultiPackageType,
                  FrozenProductType,
                  CannedProductType,
                  WholeWheatFlour,
                  PastaShape,
                  Flavor,
                  Calcium,
                  CalciumCertification,
                  IsPlainRefrigerated,
                  Texture,
                  OtherTexture,
                  Sugar,
                  SugarCertification,
                  SweetenerCertification,
                  GrainType,
                  Iron,
                  IronCertification,
                  Kcals,
                  Folate,
                  Fiber,
                  Sucrose,
                  Rounding,
                  UnderIronLimit,
                  UnderSucroseLimit,
                  ContainsNoSweeteners,
                  MilkType,
                  Protein,
                  ProteinCertification,
                  PlantYogurtAltType,
                  PlantCheeseAltType,
                  Form,
                  VitaminA,
                  VitaminACertification,
                  VitaminD,
                  VitaminDCertification,
                  VitaminB12,
                  VitaminB12Certification,
                  Magnesium,
                  MagnesiumCertification,
                  Phosphorus,
                  PhosphorusCertification,
                  Potassium,
                  PotassiumCertification,
                  Riboflavin,
                  RiboflavinCertification,
                  CheeseType,
                  FDACert,
                  Variety,
                  OtherType,
                  OtherVariety,
                  WholeGrainCert,
                  OtherFlavor,
                  WholeWheatCert,
                  Color,
                } = prod
                const tooltipEdit = `tooltip${UPC}`
                const tooltipDelete = `tooltip${UPC}9`
                return (
                  <tr key={UPC}>
                    <td>{UPC}</td>
                    <td>{brandName || BrandName}</td>
                    <td>{productName || ProductName}</td>
                    <td>
                      <Button
                        className="btn-icon btn-link edit"
                        color="success"
                        id={tooltipEdit}
                        size="sm"
                        onClick={() => {
                          editProduct(UPC)

                          methods.setValue('UPC', UPC)
                          methods.setValue('brandName', brandName || BrandName)
                          methods.setValue(
                            'productName',
                            productName || ProductName
                          )
                          methods.setValue('Manufacturer', Manufacturer)
                          methods.setValue('distbInCA', distbInCA || DistbInCA)
                          methods.setValue(
                            'RetailPrice',
                            RetailPrice || RetailPrice
                          )
                          methods.setValue('FederalNutrition', FederalNutrition)
                          switch (fkCategory) {
                            case '00':
                              switch (fkSubCategory) {
                                case '001':
                                  FatLevel = {
                                    value: FatLevel,
                                    label: FatLevel,
                                  }
                                  Flavor = {
                                    value: Flavor,
                                    label: Flavor,
                                  }
                                  PlantYogurtAltType = {
                                    value: PlantYogurtAltType,
                                    label: PlantYogurtAltType,
                                  }
                                  methods.setValue(
                                    'PlantYogurtAltType',
                                    PlantYogurtAltType
                                  )
                                  methods.setValue('VitaminD', VitaminD)
                                  methods.setValue(
                                    'SweetenerCertification',
                                    SweetenerCertification
                                  )
                                  methods.setValue('Sugar', Sugar)
                                  methods.setValue(
                                    'SugarCertification',
                                    SugarCertification
                                  )
                                  methods.setValue('Calcium', Calcium)
                                  methods.setValue(
                                    'CalciumCertification',
                                    CalciumCertification
                                  )
                                  methods.setValue('Protein', Protein)
                                  methods.setValue(
                                    'ProteinCertification',
                                    ProteinCertification
                                  )
                                  methods.setValue(
                                    'PlantYogurtAltType',
                                    PlantYogurtAltType
                                  )
                                  methods.setValue('Size', Size)
                                  methods.setValue('FatLevel', FatLevel)
                                  methods.setValue('Flavor', Flavor)
                                  methods.setValue(
                                    'MultiPackageType',
                                    MultiPackageType
                                  )
                                  break
                                case '002':
                                  PlantCheeseAltType = {
                                    value: PlantCheeseAltType,
                                    label: PlantCheeseAltType,
                                  }
                                  Type = {
                                    value: Type,
                                    label: Type,
                                  }
                                  Form = {
                                    value: Form,
                                    label: Form,
                                  }
                                  methods.setValue('Calcium', Calcium)
                                  methods.setValue(
                                    'CalciumCertification',
                                    CalciumCertification
                                  )
                                  methods.setValue('Protein', Protein)
                                  methods.setValue(
                                    'ProteinCertification',
                                    ProteinCertification
                                  )
                                  methods.setValue(
                                    'PlantCheeseAltType',
                                    PlantCheeseAltType
                                  )
                                  methods.setValue('Type', Type)
                                  methods.setValue('Form', Form)
                                  methods.setValue('Size', Size)

                                  break
                                case '003':
                                  Type = {
                                    value: Type,
                                    label: Type,
                                  }
                                  Texture = {
                                    value: Texture,
                                    label: Texture,
                                  }
                                  methods.setValue('Iron', Iron)
                                  methods.setValue('Protein', Protein)
                                  methods.setValue('Texture', Texture)
                                  methods.setValue('Type', Type)
                                  methods.setValue('Size', Size)

                                  break
                                default:
                                  break
                              }
                              break
                            case '02':
                              switch (fkSubCategory) {
                                case '001':
                                  FatLevel = {
                                    value: FatLevel,
                                    label: FatLevel,
                                  }
                                  Type = {
                                    value: Type,
                                    label: Type,
                                  }
                                  Size = {
                                    value: Size,
                                    label: Size,
                                  }
                                  methods.setValue('FatLevel', FatLevel)
                                  methods.setValue('Type', Type)
                                  methods.setValue('MadeInUSA', MadeInUSA)
                                  methods.setValue('Kosher', Kosher)
                                  methods.setValue('Size', Size)

                                  break
                                case '004':
                                  methods.setValue('Calcium', Calcium)
                                  methods.setValue(
                                    'IsPlainRefrigerated',
                                    IsPlainRefrigerated
                                  )
                                  methods.setValue('Texture', {
                                    value: Texture,
                                    label: Texture,
                                  })
                                  methods.setValue('OtherTexture', OtherTexture)
                                  methods.setValue('PackageSize', PackageSize)
                                  methods.setValue('Organic', Organic)
                                  methods.setValue('Size', Size)

                                  break
                                default:
                                  break
                              }
                              break
                            case '03':
                              switch (fkSubCategory) {
                                case '005':
                                  Grade = {
                                    value: Grade,
                                    label: Grade,
                                  }
                                  methods.setValue('Grade', Grade)
                                  methods.setValue(
                                    'ContainerSize',
                                    ContainerSize
                                  )
                                  break
                                default:
                                  break
                              }
                              break
                            case '05':
                              switch (fkSubCategory) {
                                case '001BC':
                                  methods.setValue('Kcals', Kcals)
                                  methods.setValue('Folate', Folate)
                                  methods.setValue('Fiber', Fiber)
                                  methods.setValue(
                                    'WholeGrainCert',
                                    WholeGrainCert
                                  )
                                  methods.setValue('Iron', Iron)
                                  methods.setValue('Sucrose', Sucrose)
                                  methods.setValue('Rounding', Rounding)
                                  methods.setValue(
                                    'UnderIronLimit',
                                    UnderIronLimit
                                  )
                                  methods.setValue(
                                    'UnderSucroseLimit',
                                    UnderSucroseLimit
                                  )
                                  methods.setValue(
                                    'ContainsNoSweeteners',
                                    ContainsNoSweeteners
                                  )
                                  methods.setValue('GrainType', {
                                    value: GrainType,
                                    label: GrainType,
                                  })
                                  methods.setValue('Iron', Iron)
                                  methods.setValue(
                                    'IronCertification',
                                    IronCertification
                                  )
                                  methods.setValue(
                                    'NoAdditionalIngredients',
                                    NoAdditionalIngredients
                                  )
                                  methods.setValue('Size', Size)
                                  break
                                case '001IO':
                                  methods.setValue('Kcals', Kcals)
                                  methods.setValue('Folate', Folate)
                                  methods.setValue('Fiber', Fiber)
                                  methods.setValue(
                                    'WholeGrainCert',
                                    WholeGrainCert
                                  )
                                  methods.setValue('Iron', Iron)
                                  methods.setValue('Sucrose', Sucrose)
                                  methods.setValue('Rounding', Rounding)
                                  methods.setValue(
                                    'UnderIronLimit',
                                    UnderIronLimit
                                  )
                                  methods.setValue(
                                    'UnderSucroseLimit',
                                    UnderSucroseLimit
                                  )
                                  methods.setValue(
                                    'ContainsNoSweeteners',
                                    ContainsNoSweeteners
                                  )
                                  methods.setValue('GrainType', {
                                    value: GrainType,
                                    label: GrainType,
                                  })
                                  methods.setValue('Iron', Iron)
                                  methods.setValue(
                                    'IronCertification',
                                    IronCertification
                                  )
                                  methods.setValue(
                                    'NoAdditionalIngredients',
                                    NoAdditionalIngredients
                                  )
                                  methods.setValue('OatType', OatType)
                                  methods.setValue('Flavor', {
                                    value: Flavor,
                                    label: Flavor,
                                  })
                                  methods.setValue('Size', Size)
                                  break
                                default:
                                  break
                              }
                              break
                            case '06':
                              switch (fkSubCategory) {
                                case '001':
                                  PBType = {
                                    value: PBType,
                                    label: PBType,
                                  }
                                  methods.setValue('PBType', PBType)
                                  methods.setValue(
                                    'PBContainerSize',
                                    PBContainerSize
                                  )

                                  if (
                                    peanutButterTextureOptions.some(e => {
                                      return (
                                        JSON.stringify({
                                          value: PBTexture,
                                          label: PBTexture,
                                        }) === JSON.stringify(e)
                                      )
                                    })
                                  ) {
                                    PBTexture = {
                                      value: PBTexture,
                                      label: PBTexture,
                                    }
                                    methods.setValue('PBTexture', PBTexture)
                                  } else {
                                    const PBTextureOther = {
                                      value: 'Other',
                                      label: 'Other',
                                    }

                                    methods.setValue(
                                      'PBTexture',
                                      PBTextureOther
                                    )
                                    methods.setValue('otherTexture', PBTexture)
                                  }
                                  break
                                case '002':
                                  BeanPeaLentilType = {
                                    value: BeanPeaLentilType,
                                    label: BeanPeaLentilType,
                                  }
                                  BeanPeaLentilSize = {
                                    value: BeanPeaLentilSize,
                                    label: BeanPeaLentilSize,
                                  }
                                  methods.setValue(
                                    'BeanPeaLentilType',
                                    BeanPeaLentilType
                                  )
                                  methods.setValue(
                                    'BeanPeaLentilSize',
                                    BeanPeaLentilSize
                                  )
                                  break
                                case '003':
                                  SaltContent = {
                                    value: SaltContent,
                                    label: SaltContent,
                                  }
                                  methods.setValue('SaltContent', SaltContent)
                                  methods.setValue(
                                    'PBContainerSize',
                                    PBContainerSize
                                  )
                                  break
                                default:
                                  break
                              }
                              break
                            case '08':
                              FishType = {
                                value: FishType,
                                label: FishType,
                              }
                              PackedIn = {
                                value: PackedIn,
                                label: PackedIn,
                              }
                              Size = {
                                value: Size,
                                label: Size,
                              }
                              methods.setValue('FishType', FishType)
                              methods.setValue('PackedIn', PackedIn)
                              methods.setValue('Size', Size)
                              break
                            case '09':
                              switch (fkSubCategory) {
                                case '001BS':
                                  methods.setValue('GrainType', {
                                    value: GrainType,
                                    label: GrainType,
                                  })
                                  methods.setValue('Iron', Iron)
                                  methods.setValue(
                                    'IronCertification',
                                    IronCertification
                                  )
                                  methods.setValue(
                                    'NoAdditionalIngredients',
                                    NoAdditionalIngredients
                                  )
                                  methods.setValue('Size', {
                                    value: Size,
                                    label: Size,
                                  })
                                  methods.setValue('Organic', Organic)
                                  break
                                default:
                                  break
                              }
                              break
                            case '12':
                              switch (fkSubCategory) {
                                case '001':
                                case '002':
                                case '003':
                                case '101':
                                case '102':
                                case '103':
                                  PackageType = {
                                    value: PackageType,
                                    label: PackageType,
                                  }
                                  brandNameSelect = {
                                    value: brandName || BrandName,
                                    label: brandName || BrandName,
                                  }
                                  methods.setValue('brandName', brandNameSelect)
                                  methods.setValue('PackageType', PackageType)
                                  methods.setValue(
                                    'NoAdditionalIngredients',
                                    NoAdditionalIngredients
                                  )
                                  methods.setValue('Organic', Organic)
                                  methods.setValue(
                                    'MultiPackageType',
                                    MultiPackageType
                                  )
                                  break
                                case '001BS':
                                case '002BS':
                                case '003BS':
                                case '101BS':
                                case '102BS':
                                case '103BS':
                                  methods.setValue('PackageType', {
                                    value: PackageType,
                                    label: PackageType,
                                  })
                                  methods.setValue(
                                    'NoAdditionalIngredients',
                                    NoAdditionalIngredients
                                  )
                                  methods.setValue('Organic', Organic)
                                  methods.setValue(
                                    'MultiPackageType',
                                    MultiPackageType
                                  )
                                  break
                                case '001RFI':
                                case '002RFI':
                                case '003RFI':
                                case '101RFI':
                                case '102RFI':
                                case '103RFI':
                                  brandNameSelect = {
                                    value: brandName || BrandName,
                                    label: brandName || BrandName,
                                  }
                                  methods.setValue('brandName', brandNameSelect)
                                  methods.setValue('PackageType', {
                                    value: PackageType,
                                    label: PackageType,
                                  })
                                  methods.setValue(
                                    'NoAdditionalIngredients',
                                    NoAdditionalIngredients
                                  )
                                  methods.setValue('Organic', Organic)
                                  methods.setValue(
                                    'MultiPackageType',
                                    MultiPackageType
                                  )
                                  break
                                default:
                                  break
                              }
                              break
                            case '13':
                              switch (fkSubCategory) {
                                case '001':
                                case '101':
                                  PackageType = {
                                    value: PackageType,
                                    label: PackageType,
                                  }
                                  brandNameSelect = {
                                    value: brandName || BrandName,
                                    label: brandName || BrandName,
                                  }
                                  methods.setValue('brandName', brandNameSelect)
                                  methods.setValue(
                                    'MultiPackageType',
                                    MultiPackageType
                                  )
                                  methods.setValue('Organic', Organic)
                                  methods.setValue(
                                    'NoAdditionalIngredients',
                                    NoAdditionalIngredients
                                  )
                                  methods.setValue('PackageType', PackageType)
                                  break
                                case '001BS':
                                case '101BS':
                                  methods.setValue(
                                    'MultiPackageType',
                                    MultiPackageType
                                  )
                                  methods.setValue('Organic', Organic)
                                  methods.setValue(
                                    'NoAdditionalIngredients',
                                    NoAdditionalIngredients
                                  )
                                  methods.setValue('PackageType', {
                                    value: PackageType,
                                    label: PackageType,
                                  })
                                  break
                                case '001RFI':
                                case '101RFI':
                                  brandNameSelect = {
                                    value: brandName || BrandName,
                                    label: brandName || BrandName,
                                  }
                                  methods.setValue('brandName', brandNameSelect)
                                  methods.setValue(
                                    'MultiPackageType',
                                    MultiPackageType
                                  )
                                  methods.setValue('Organic', Organic)
                                  methods.setValue(
                                    'NoAdditionalIngredients',
                                    NoAdditionalIngredients
                                  )
                                  methods.setValue('PackageType', {
                                    value: PackageType,
                                    label: PackageType,
                                  })
                                  break
                                default:
                                  break
                              }

                              break
                            case '16':
                              switch (fkSubCategory) {
                                case '001':
                                  BreadType = {
                                    value: BreadType,
                                    label: BreadType,
                                  }
                                  methods.setValue('BreadType', BreadType)
                                  methods.setValue('BreadSize', BreadSize)
                                  break
                                case '003':
                                  CookingType = {
                                    value: CookingType,
                                    label: CookingType,
                                  }
                                  GrainLength = {
                                    value: GrainLength,
                                    label: GrainLength,
                                  }
                                  methods.setValue('CookingType', CookingType)
                                  methods.setValue('GrainSize', GrainSize)
                                  methods.setValue('GrainLength', GrainLength)

                                  if (
                                    brownRiceOatVarietyOptions.some(e => {
                                      return (
                                        JSON.stringify({
                                          value: OatVariety,
                                          label: OatVariety,
                                        }) === JSON.stringify(e)
                                      )
                                    })
                                  ) {
                                    OatVariety = {
                                      value: OatVariety,
                                      label: OatVariety,
                                    }
                                    methods.setValue('OatVariety', OatVariety)
                                  } else {
                                    const OatVarietyOther = {
                                      value: 'Other',
                                      label: 'Other',
                                    }

                                    methods.setValue(
                                      'OatVariety',
                                      OatVarietyOther
                                    )
                                    methods.setValue('otherType', OatVariety)
                                  }
                                  break
                                case '004':
                                  methods.setValue('GrainSize', GrainSize)
                                  methods.setValue('Organic', Organic)
                                  break
                                case '005':
                                  CookingType = {
                                    value: CookingType,
                                    label: CookingType,
                                  }
                                  methods.setValue('OatType', OatType)
                                  methods.setValue('CookingType', CookingType)
                                  methods.setValue('OatVariety', OatVariety)
                                  methods.setValue('GrainSize', GrainSize)

                                  if (
                                    oatsOatmealOatTypeOptions.some(e => {
                                      return (
                                        JSON.stringify({
                                          value: OatType,
                                          label: OatType,
                                        }) === JSON.stringify(e)
                                      )
                                    })
                                  ) {
                                    OatType = {
                                      value: OatType,
                                      label: OatType,
                                    }
                                    console.log('true')
                                    methods.setValue('OatType', OatType)
                                  } else {
                                    const OatTypeOther = {
                                      value: 'Other',
                                      label: 'Other',
                                    }
                                    methods.setValue('OatType', OatTypeOther)
                                    methods.setValue('otherType', OatType)
                                  }

                                  if (
                                    oatsOatmealOatVarietyOptions.some(e => {
                                      return (
                                        JSON.stringify({
                                          value: OatVariety,
                                          label: OatVariety,
                                        }) === JSON.stringify(e)
                                      )
                                    })
                                  ) {
                                    OatVariety = {
                                      value: OatVariety,
                                      label: OatVariety,
                                    }
                                    methods.setValue('OatVariety', OatVariety)
                                  } else {
                                    const OatVarietyOther = {
                                      value: 'Other',
                                      label: 'Other',
                                    }

                                    methods.setValue(
                                      'OatVariety',
                                      OatVarietyOther
                                    )
                                    methods.setValue('otherVariety', OatVariety)
                                  }
                                  break
                                case '005BS':
                                  methods.setValue('OatType', {
                                    value: OatType,
                                    label: OatType,
                                  })
                                  methods.setValue('OtherType', OtherType)
                                  methods.setValue('Size', Size)
                                  methods.setValue('Variety', {
                                    value: Variety,
                                    label: Variety,
                                  })
                                  methods.setValue('OtherVariety', OtherVariety)
                                  methods.setValue('CookingType', {
                                    value: CookingType,
                                    label: CookingType,
                                  })
                                  break
                                case '006':
                                  methods.setValue('GrainSize', GrainSize)
                                  methods.setValue(
                                    'BarleyVerification',
                                    BarleyVerification
                                  )
                                  methods.setValue('Organic', Organic)
                                  break
                                case '007':
                                case '008':
                                  brandNameSelect = {
                                    value: brandName || BrandName,
                                    label: brandName || BrandName,
                                  }
                                  methods.setValue('brandName', brandNameSelect)
                                  methods.setValue('Size', Size)
                                  break
                                case '009':
                                  brandNameSelect = {
                                    value: brandName || BrandName,
                                    label: brandName || BrandName,
                                  }
                                  methods.setValue('brandName', brandNameSelect)
                                  methods.setValue(
                                    'WholeWheatFlour',
                                    WholeWheatFlour
                                  )
                                  methods.setValue('PastaShape', PastaShape)
                                  methods.setValue('Size', Size)
                                  break
                                case '010':
                                case '011':
                                case '012':
                                  methods.setValue('Size', Size)
                                  methods.setValue(
                                    'WholeWheatCert',
                                    WholeWheatCert
                                  )
                                  break

                                case '010BS':
                                case '011BS':
                                case '012BS':
                                case '013':
                                case '014':
                                  methods.setValue('Size', Size)
                                  break
                                case '015':
                                  methods.setValue('GrainLength', {
                                    value: GrainLength,
                                    label: GrainLength,
                                  })
                                  methods.setValue('Size', Size)
                                  break
                                case '016BS':
                                  methods.setValue('Size', Size)
                                  methods.setValue('Organic', Organic)
                                  break
                                case '017BS':
                                  methods.setValue('Size', Size)
                                  methods.setValue('FDACert', FDACert)
                                  methods.setValue('Organic', Organic)
                                  break
                                case '016':
                                  methods.setValue('CookingType', {
                                    value: CookingType,
                                    label: CookingType,
                                  })
                                  methods.setValue('Size', Size)
                                  break

                                case '017':
                                  methods.setValue('Texture', {
                                    value: Texture,
                                    label: Texture,
                                  })
                                  methods.setValue('Color', {
                                    value: Color,
                                    label: Color,
                                  })
                                  methods.setValue('Size', Size)
                                  methods.setValue(
                                    'WholeGrainCert',
                                    WholeGrainCert
                                  )
                                  break
                                default:
                                  break
                              }
                              break
                            case '50':
                              switch (fkSubCategory) {
                                case '001':
                                case '002':
                                  methods.setValue('VitaminD', VitaminD)
                                  methods.setValue('Sugar', Sugar)
                                  methods.setValue(
                                    'SugarCertification',
                                    SugarCertification
                                  )
                                  methods.setValue(
                                    'SweetenerCertification',
                                    SweetenerCertification
                                  )
                                  methods.setValue('FatLevel', {
                                    value: FatLevel,
                                    label: FatLevel,
                                  })
                                  methods.setValue('Flavor', {
                                    value: Flavor,
                                    label: Flavor,
                                  })
                                  methods.setValue(
                                    'MultiPackageType',
                                    MultiPackageType
                                  )
                                  break
                                default:
                                  break
                              }
                              break
                            case '51':
                              WholeMilkType = {
                                value: WholeMilkType,
                                label: WholeMilkType,
                              }
                              WholeMilkSize = {
                                value: WholeMilkSize,
                                label: WholeMilkSize,
                              }
                              methods.setValue('WholeMilkType', WholeMilkType)
                              methods.setValue('WholeMilkSize', WholeMilkSize)
                              break
                            case '52':
                              switch (fkSubCategory) {
                                case '002':
                                case '004':
                                case '006':
                                case '012':
                                case '014':
                                  ReducedFatMilkType = {
                                    value: ReducedFatMilkType,
                                    label: ReducedFatMilkType,
                                  }
                                  ReducedFatMilkSize = {
                                    value: ReducedFatMilkSize,
                                    label: ReducedFatMilkSize,
                                  }
                                  methods.setValue(
                                    'ReducedFatMilkType',
                                    ReducedFatMilkType
                                  )
                                  methods.setValue(
                                    'ReducedFatMilkSize',
                                    ReducedFatMilkSize
                                  )
                                  break
                                case '011':
                                  methods.setValue('MilkType', {
                                    value: MilkType,
                                    label: MilkType,
                                  })
                                  methods.setValue('Flavor', {
                                    value: Flavor,
                                    label: Flavor,
                                  })
                                  methods.setValue('Size', Size)
                                  methods.setValue('Type', {
                                    value: Type,
                                    label: Type,
                                  })
                                  methods.setValue('Organic', Organic)
                                  methods.setValue(
                                    'MultiPackageType',
                                    MultiPackageType
                                  )
                                  methods.setValue('Sugar', Sugar)
                                  methods.setValue(
                                    'SugarCertification',
                                    SugarCertification
                                  )
                                  methods.setValue('Calcium', Calcium)
                                  methods.setValue(
                                    'CalciumCertification',
                                    CalciumCertification
                                  )
                                  methods.setValue('Protein', Protein)
                                  methods.setValue(
                                    'ProteinCertification',
                                    ProteinCertification
                                  )
                                  methods.setValue('VitaminA', VitaminA)
                                  methods.setValue(
                                    'VitaminACertification',
                                    VitaminACertification
                                  )
                                  methods.setValue('VitaminD', VitaminD)
                                  methods.setValue(
                                    'VitaminDCertification',
                                    VitaminDCertification
                                  )
                                  methods.setValue('VitaminB12', VitaminB12)
                                  methods.setValue(
                                    'VitaminB12Certification',
                                    VitaminB12Certification
                                  )
                                  methods.setValue('Magnesium', Magnesium)
                                  methods.setValue(
                                    'MagnesiumCertification',
                                    MagnesiumCertification
                                  )
                                  methods.setValue('Phosphorus', Phosphorus)
                                  methods.setValue(
                                    'PhosphorusCertification',
                                    PhosphorusCertification
                                  )
                                  methods.setValue('Potassium', Potassium)
                                  methods.setValue(
                                    'PotassiumCertification',
                                    PotassiumCertification
                                  )
                                  methods.setValue('Riboflavin', Riboflavin)
                                  methods.setValue(
                                    'RiboflavinCertification',
                                    RiboflavinCertification
                                  )
                                  break
                                default:
                                  break
                              }
                              break
                            case '54':
                              JuiceFlavor = {
                                value: JuiceFlavor,
                                label: JuiceFlavor,
                              }
                              ContainerType = {
                                value: ContainerType,
                                label: ContainerType,
                              }
                              methods.setValue('JuiceFlavor', JuiceFlavor)
                              methods.setValue('ContainerType', ContainerType)
                              methods.setValue(
                                'VitaminCCertification',
                                VitaminCCertification
                              )
                              methods.setValue(
                                'OneHundredPercentJuiceCertification',
                                OneHundredPercentJuiceCertification
                              )
                              methods.setValue(
                                'FrontLabelCertification',
                                FrontLabelCertification
                              )
                              break
                            case '19':
                              switch (fkSubCategory) {
                                case '001FF':
                                case '001FV':
                                  methods.setValue('Size', Size)
                                  methods.setValue(
                                    'OrganicVerification',
                                    OrganicVerification
                                  )
                                  methods.setValue(
                                    'FrozenPackageCertification',
                                    FrozenPackageCertification
                                  )
                                  PackageType = {
                                    value: PackageType,
                                    label: PackageType,
                                  }
                                  if (
                                    frozenPackageTypeOptions.some(e => {
                                      return (
                                        JSON.stringify(PackageType) ===
                                        JSON.stringify(e)
                                      )
                                    })
                                  ) {
                                    methods.setValue('PackageType', PackageType)
                                  } else {
                                    const PackageTypeOther = {
                                      value: 'Other',
                                      label: 'Other',
                                    }
                                    methods.setValue(
                                      'PackageType',
                                      PackageTypeOther
                                    )
                                    methods.setValue(
                                      'otherPackageType',
                                      PackageType.value
                                    )
                                  }

                                  if (FrozenProductType === 'Fruit') {
                                    if (
                                      frozenFruitTypeOptions.some(e => {
                                        return (
                                          JSON.stringify({
                                            value: ItemType,
                                            label: ItemType,
                                          }) === JSON.stringify(e)
                                        )
                                      })
                                    ) {
                                      ItemType = {
                                        value: ItemType,
                                        label: ItemType,
                                      }
                                      methods.setValue('ItemType', ItemType)
                                    } else {
                                      const PackageTypeOther = {
                                        value: 'Other',
                                        label: 'Other',
                                      }
                                      methods.setValue(
                                        'ItemType',
                                        PackageTypeOther
                                      )
                                      methods.setValue('otherType', ItemType)
                                    }
                                  } else {
                                    if (
                                      frozenVegTypeOptions.some(e => {
                                        return (
                                          JSON.stringify({
                                            value: ItemType,
                                            label: ItemType,
                                          }) === JSON.stringify(e)
                                        )
                                      })
                                    ) {
                                      ItemType = {
                                        value: ItemType,
                                        label: ItemType,
                                      }
                                      methods.setValue('ItemType', ItemType)
                                    } else {
                                      const PackageTypeOther = {
                                        value: 'Other',
                                        label: 'Other',
                                      }
                                      methods.setValue(
                                        'ItemType',
                                        PackageTypeOther
                                      )
                                      methods.setValue('otherType', ItemType)
                                    }
                                  }
                                  break
                                case '001CF':
                                case '001CV':
                                  PackedIn = {
                                    value: PackedIn,
                                    label: PackedIn,
                                  }
                                  SaltContent = {
                                    value: SaltContent,
                                    label: SaltContent,
                                  }
                                  PackageSize = {
                                    value: PackageSize,
                                    label: PackageSize,
                                  }
                                  Packaged = {
                                    value: Packaged,
                                    label: Packaged,
                                  }

                                  methods.setValue('Size', Size)
                                  methods.setValue('Packaged', Packaged)
                                  methods.setValue(
                                    'OrganicVerification',
                                    OrganicVerification
                                  )
                                  if (CannedProductType === 'Fruit') {
                                    methods.setValue(
                                      'noAddtlIngredients',
                                      FruitPackageCertification
                                    )
                                    methods.setValue('packageType', PackedIn)
                                  } else {
                                    methods.setValue(
                                      'noAddtlIngredients',
                                      VegetablePackageCertification
                                    )

                                    methods.setValue('packageType', SaltContent)
                                  }
                                  if (
                                    cannedFruitContainerTypeOptions.some(e => {
                                      return (
                                        JSON.stringify({
                                          value: ContainerType,
                                          label: ContainerType,
                                        }) === JSON.stringify(e)
                                      )
                                    }) ||
                                    cannedVegContainerTypeOptions.some(e => {
                                      return (
                                        JSON.stringify({
                                          value: ContainerType,
                                          label: ContainerType,
                                        }) === JSON.stringify(e)
                                      )
                                    })
                                  ) {
                                    ContainerType = {
                                      value: ContainerType,
                                      label: ContainerType,
                                    }
                                    methods.setValue(
                                      'ContainerType',
                                      ContainerType
                                    )
                                  } else {
                                    const ContainerTypeOther = {
                                      value: 'Other',
                                      label: 'Other',
                                    }
                                    methods.setValue(
                                      'ContainerType',
                                      ContainerTypeOther
                                    )
                                    methods.setValue(
                                      'otherContainerType',
                                      ContainerType
                                    )
                                  }
                                  if (
                                    cannedFruitTypeOptions.some(e => {
                                      return (
                                        JSON.stringify({
                                          value: ItemType,
                                          label: ItemType,
                                        }) === JSON.stringify(e)
                                      )
                                    }) ||
                                    cannedVegTypeOptions.some(e => {
                                      return (
                                        JSON.stringify({
                                          value: ItemType,
                                          label: ItemType,
                                        }) === JSON.stringify(e)
                                      )
                                    })
                                  ) {
                                    ItemType = {
                                      value: ItemType,
                                      label: ItemType,
                                    }
                                    methods.setValue('ItemType', ItemType)
                                  } else {
                                    const ItemTypeOther = {
                                      value: 'Other',
                                      label: 'Other',
                                    }
                                    methods.setValue('ItemType', ItemTypeOther)
                                    methods.setValue('otherType', ItemType)
                                  }

                                  break
                                case '004':
                                  methods.setValue('DFSize', DFSize)
                                  methods.setValue('Organic', Organic)
                                  methods.setValue(
                                    'NoAdditionalIngredients',
                                    NoAdditionalIngredients
                                  )
                                  if (
                                    driedFruitsTypeOptions.some(e => {
                                      return (
                                        JSON.stringify({
                                          value: DFType,
                                          label: DFType,
                                        }) === JSON.stringify(e)
                                      )
                                    })
                                  ) {
                                    DFType = {
                                      value: DFType,
                                      label: DFType,
                                    }
                                    methods.setValue('DFType', DFType)
                                  } else {
                                    const otherTypeSelect = {
                                      value: 'Other',
                                      label: 'Other',
                                    }
                                    methods.setValue('DFType', otherTypeSelect)
                                    methods.setValue('otherType', DFType)
                                  }

                                  if (
                                    driedFruitsPackageTypeOptions.some(e => {
                                      return (
                                        JSON.stringify({
                                          value: DFPackageType,
                                          label: DFPackageType,
                                        }) === JSON.stringify(e)
                                      )
                                    })
                                  ) {
                                    DFPackageType = {
                                      value: DFPackageType,
                                      label: DFPackageType,
                                    }
                                    methods.setValue(
                                      'DFPackageType',
                                      DFPackageType
                                    )
                                  } else {
                                    const otherPackageType = {
                                      value: 'Other',
                                      label: 'Other',
                                    }

                                    methods.setValue(
                                      'DFPackageType',
                                      otherPackageType
                                    )
                                    methods.setValue(
                                      'otherPackageType',
                                      DFPackageType
                                    )
                                  }
                                  break
                                default:
                                  break
                              }
                              break
                            default:
                              console.log('fkCategory Error: ' + fkCategory)
                          }
                        }}
                      >
                        <i className="fa fa-edit" />
                      </Button>{' '}
                      <UncontrolledTooltip delay={0} target={tooltipEdit}>
                        Edit
                      </UncontrolledTooltip>
                      <Button
                        className="btn-icon btn-link delete"
                        color="danger"
                        id={tooltipDelete}
                        size="sm"
                        onClick={() => deleteCurrent(UPC)}
                      >
                        <i className="fa fa-times" />
                      </Button>{' '}
                      <UncontrolledTooltip delay={0} target={tooltipDelete}>
                        Delete
                      </UncontrolledTooltip>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
          {filtered?.length > 0 ? (
            <Button
              className="btn-round float-right"
              color="success"
              onClick={e => {
                submitProductList(e)
                setScrollToTop(true)
              }}
            >
              Submit
            </Button>
          ) : null}
        </CardBody>
      </Card>
    </>
  )
}

SubmissionList.propTypes = {
  productTitle: PropTypes.string,
  category: PropTypes.string,
  subCategory: PropTypes.string,
  model: PropTypes.object,
}

export default SubmissionList
